const BOARD_DATA_BASE_URL = 'https://abet-board-data.abetco.workers.dev'

function splitEvent(eventName) {
  const text = String(eventName || '').trim()
  const separator = text.includes(' @ ') ? ' @ ' : text.includes(' vs ') ? ' vs ' : null
  if (!separator) return { awayTeam: '', homeTeam: '', display: text }
  const [awayTeam = '', homeTeam = ''] = text.split(separator, 2)
  return { awayTeam, homeTeam, display: `${awayTeam} @ ${homeTeam}` }
}

function normalizeMarket(betType) {
  const value = String(betType || '').toLowerCase()
  if (value.includes('moneyline') || value === 'ml') return 'ML'
  if (value.includes('spread') || value.includes('run line') || value.includes('puck line')) return 'SPREAD'
  if (value.includes('total')) return 'TOTAL'
  return String(betType || '').toUpperCase()
}

function fairAmericanFromProbability(probability) {
  const probabilityNumber = Number(probability)
  if (!Number.isFinite(probabilityNumber) || probabilityNumber <= 0 || probabilityNumber >= 1) return null
  if (probabilityNumber >= 0.5) {
    return Math.round((-100 * probabilityNumber) / (1 - probabilityNumber))
  }
  return Math.round((100 * (1 - probabilityNumber)) / probabilityNumber)
}

function impliedProbability(americanOdds) {
  const odds = Number(americanOdds)
  if (!Number.isFinite(odds)) return null
  if (odds >= 100) return 100 / (odds + 100)
  if (odds <= -100) return Math.abs(odds) / (Math.abs(odds) + 100)
  return null
}

function dateOnlyIso(date) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(date || '')) ? `${date}T12:00:00Z` : null
}

export async function fetchCrossbookEdge() {
  const response = await fetch(`${BOARD_DATA_BASE_URL}/data/crossbook_edge.json`, {
    signal: AbortSignal.timeout(20_000),
    cache: 'no-store',
  })
  if (!response.ok) throw new Error(`Cross-book edge feed unavailable (HTTP ${response.status})`)
  return response.json()
}

export function adaptEngineEvs(payload) {
  return (payload?.evs || []).map((entry) => {
    const event = splitEvent(entry.event)
    return {
      ev: Number(entry.ev_pct || 0),
      edge: null,
      game: event.display,
      away_team: event.awayTeam,
      home_team: event.homeTeam,
      league: String(entry.league || '').toUpperCase(),
      market: normalizeMarket(entry.bet_type),
      handicap: entry.line ?? null,
      selection: entry.side,
      bookOdds: Number(entry.american),
      fairOdds: fairAmericanFromProbability(entry.fair_prob),
      book: entry.book,
      pinnacleOdds: entry.prices?.pinnacle ?? null,
      hold: null,
      start_time: dateOnlyIso(entry.date),
      start_label: [entry.date, entry.time].filter(Boolean).join(' '),
      is_live: false,
      n_books: Number(entry.n_books || 0),
      prices: entry.prices || {},
      deep_link: entry.dl || null,
      period: entry.period || 'Game',
      subject: entry.subject || '',
    }
  })
}

export function adaptEngineArbs(payload) {
  return (payload?.arbs || []).map((entry, index) => {
    const event = splitEvent(entry.event)
    const odds1 = Number(entry.a?.american)
    const odds2 = Number(entry.b?.american)
    const probability1 = impliedProbability(odds1)
    const probability2 = impliedProbability(odds2)
    const totalImplied = probability1 != null && probability2 != null ? probability1 + probability2 : null
    return {
      canonical_id: `edge-${entry.league}-${entry.date}-${entry.event}-${entry.bet_type}-${entry.line ?? 'main'}-${index}`,
      participants: event.display,
      away_team: event.awayTeam,
      home_team: event.homeTeam,
      league: String(entry.league || '').toUpperCase(),
      start_time: dateOnlyIso(entry.date),
      start_label: [entry.date, entry.time].filter(Boolean).join(' '),
      market_normalized: normalizeMarket(entry.bet_type),
      handicap: entry.line ?? null,
      selection1: entry.a?.side,
      selection2: entry.b?.side,
      odds1,
      odds2,
      book1: entry.a?.book,
      book2: entry.b?.book,
      deep_link1: entry.a?.dl || null,
      deep_link2: entry.b?.dl || null,
      profit_pct: Number(entry.profit_pct || 0),
      implied1: probability1 == null ? '—' : `${(probability1 * 100).toFixed(2)}%`,
      implied2: probability2 == null ? '—' : `${(probability2 * 100).toFixed(2)}%`,
      total_implied: totalImplied == null ? '—' : `${(totalImplied * 100).toFixed(2)}%`,
      period: entry.period || 'Game',
    }
  })
}

export function adaptEngineMiddles(payload) {
  return (payload?.middles || []).map((entry, index) => {
    const event = splitEvent(entry.event)
    return {
      id: `middle-${entry.league}-${entry.date}-${entry.event}-${entry.bet_type}-${index}`,
      league: String(entry.league || '').toUpperCase(),
      event: event.display,
      date: entry.date || null,
      period: entry.period || 'Game',
      betType: entry.bet_type || 'Total',
      over: {
        line: Number(entry.over?.line),
        book: entry.over?.book,
        american: Number(entry.over?.american),
        deepLink: entry.over?.dl || null,
      },
      under: {
        line: Number(entry.under?.line),
        book: entry.under?.book,
        american: Number(entry.under?.american),
        deepLink: entry.under?.dl || null,
      },
      window: Number(entry.window || 0),
      middleNumbers: Array.isArray(entry.middle_numbers) ? entry.middle_numbers : [],
      costPct: Number(entry.cost_pct || 0),
    }
  })
}

export function getEdgeFeedProof(payload) {
  return {
    capturedAt: payload?.captured_utc || null,
    generatedAt: payload?.generated_utc || null,
    books: Array.isArray(payload?.books) ? payload.books.length : 0,
    leagues: Array.isArray(payload?.leagues) ? payload.leagues.length : 0,
    markets: Array.isArray(payload?.markets) ? payload.markets.length : 0,
    method: payload?.method || null,
  }
}
